import { ClickToCopyRemotePromoId } from "@/components/click_to_copy_remote_promo_id.tsx"
import { useState } from "preact/hooks"
import { Promotion } from '../../promo_creation/types'
import { getPromotionTypeColors } from '../../promo_creation/constants'
import { parseISO, format, startOfWeek } from 'date-fns'
import { exactCurrencyFormatter } from '../../../../utils'
import { Paperclip } from "lucide-react"

interface PromotionPosition {
  startWeekIndex: number
  endWeekIndex: number
  spanWeeks: number
}

interface PromotionBlockProps {
  promotion: Promotion
  position: PromotionPosition
  onEdit: () => void
  rowHeight: number
  rowIndex: number
  verticalOffset?: number
}

export function PromotionBlock({ promotion, position, onEdit, rowHeight, rowIndex, verticalOffset = 0 }: PromotionBlockProps) {
  const [isHovered, setIsHovered] = useState(false)
  // Calculate positioning
  const weekWidth = 100 // Each week column is 100px wide
  const leftOffset = position.startWeekIndex * weekWidth
  const width = position.spanWeeks * weekWidth - 4 // Subtract 4px for gaps
  
  // Get promotion colors based on type
  const colors = getPromotionColors(promotion.promotion_type)
  
  // Format discount information
  const discountText = getDiscountText(promotion)

  // Secondary detail line under the prominent name: type and discount (product names omitted — too noisy).
  const detailText = [promotion.promotion_type, discountText].filter(Boolean).join(' • ')
  
  // Position within the row (top-aligned) with vertical offset for stacking
  const baseTopOffset = 5 // Small margin from top of row
  const topOffset = baseTopOffset + verticalOffset
  
  // Determine if we have enough space for detailed layout
  const hasEnoughSpace = width >= 120 // Need at least 120px for detailed layout
  
  // Smart text truncation based on available width
  const getTruncatedText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength - 3) + '...'
  }

  const remotePromoIdText = promotion.remote_promo_id
    ? getTruncatedText(`ID: ${promotion.remote_promo_id}`, Math.max(14, Math.floor(width / 7)))
    : null

  return (
    <div
      className={`absolute rounded px-2 py-1 text-xs overflow-hidden cursor-pointer transition-all duration-200 ${
        isHovered 
          ? 'ring-2 ring-blue-300 shadow-lg scale-105' 
          : 'shadow-sm hover:ring-2 hover:ring-blue-300'
      }`}
      style={{
        left: `${leftOffset}px`,
        width: `${width}px`,
        height: '52px',
        backgroundColor: colors.background,
        color: colors.text,
        top: `${topOffset}px`,
        zIndex: isHovered ? 100 + rowIndex * 10 : 10 + rowIndex * 10
      }}
      onClick={onEdit}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={`${promotion.products && promotion.products.length > 0 ? promotion.products.join(', ') : promotion.name}${promotion.remote_promo_id ? ` (${promotion.remote_promo_id})` : ''} - ${promotion.promotion_type || 'Unknown Type'} - ${discountText} - ${promotion.start_date && promotion.end_date ? formatDateRange(promotion.start_date, promotion.end_date) : 'No dates'}`}
    >
      {hasEnoughSpace ? (
        // Detailed layout for wider blocks - two rows
        <div className="h-full flex flex-col justify-center gap-0.5 py-1 relative">
          {/* Top row: Promotion Name (prominent) + Date Range */}
          <div className="flex items-center justify-between gap-1">
            <div className="font-semibold truncate flex-1 min-w-0">
              {remotePromoIdText && promotion.remote_promo_id && (
                <ClickToCopyRemotePromoId
                  copyValue={promotion.remote_promo_id}
                  stopPropagation
                  className="mr-1 inline-flex rounded bg-white/70 px-1 py-0.5 text-[10px] font-bold text-gray-900 hover:ring-1 hover:ring-blue-400"
                >
                  {remotePromoIdText}
                </ClickToCopyRemotePromoId>
              )}
              <span>{promotion.name}</span>
            </div>
            <div className="flex items-center gap-1">
              {promotion.start_date && promotion.end_date && (
                <div className="text-xs font-medium opacity-90">
                  {formatDateRange(promotion.start_date, promotion.end_date)}
                </div>
              )}
              {promotion.file_count && promotion.file_count > 0 && (
                <div className="flex items-center gap-0.5 opacity-70" title={`${promotion.file_count} file${promotion.file_count !== 1 ? 's' : ''}`}>
                  <Paperclip className="h-3 w-3" />
                  {promotion.file_count > 1 && (
                    <span className="text-[10px] font-medium">{promotion.file_count}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bottom row: type • discount • products (secondary detail) */}
          {detailText && (
            <div className="opacity-90 text-xs truncate">
              {getTruncatedText(detailText, Math.floor(width / 6))}
            </div>
          )}
        </div>
      ) : (
        // Compact layout for narrow blocks - two rows
        <div className="h-full flex flex-col justify-center gap-0.5 py-1 relative">
          {/* Top row: Promotion Name (prominent) */}
          <div className="font-medium text-center text-xs relative truncate">
            {remotePromoIdText && promotion.remote_promo_id && (
              <ClickToCopyRemotePromoId
                copyValue={promotion.remote_promo_id}
                stopPropagation
                className="mr-1 inline-flex rounded bg-white/70 px-1 py-0.5 text-[10px] font-bold text-gray-900 hover:ring-1 hover:ring-blue-400"
              >
                {remotePromoIdText}
              </ClickToCopyRemotePromoId>
            )}
            <span>{promotion.name}</span>
            {promotion.file_count && promotion.file_count > 0 && (
              <div className="absolute top-0 right-0 flex items-center gap-0.5 opacity-60" title={`${promotion.file_count} file${promotion.file_count !== 1 ? 's' : ''}`}>
                <Paperclip className="h-2.5 w-2.5" />
                {promotion.file_count > 1 && width >= 100 && (
                  <span className="text-[9px] font-medium">{promotion.file_count}</span>
                )}
              </div>
            )}
          </div>
          
          {/* Bottom row: type • discount • products (secondary detail) if there's room */}
          {width >= 120 && detailText && (
            <div className="opacity-80 text-center text-xs truncate">
              {getTruncatedText(detailText, Math.floor(width / 8))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function getPromotionColors(promotionType?: string): { background: string; text: string } {
  const colors = getPromotionTypeColors(promotionType)
  return {
    background: colors.background,
    text: colors.text
  }
}

function getDiscountText(promotion: Promotion): string {
  if (promotion.multibuy_quantity && promotion.multibuy_price) {
    return `${promotion.multibuy_quantity} for ${exactCurrencyFormatter(promotion.multibuy_price)}`
  }
  
  if (promotion.discount_percentage) {
    return `${promotion.discount_percentage}%`
  }
  
  if (promotion.discount_amount) {
    return exactCurrencyFormatter(promotion.discount_amount)
  }
  
  return ''
}

function formatDateRange(startDate: string, endDate: string): string {
  // Use parseISO from date-fns as per CLAUDE.md guidelines
  const start = parseISO(startDate)
  const end = parseISO(endDate)
  
  // Use format from date-fns for consistent date formatting
  const startFormatted = format(start, 'MMM d')
  const endFormatted = format(end, 'MMM d')
  
  // If same month and year, show "Jan 15-20", otherwise show "Jan 15 - Feb 20"
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${startFormatted}-${end.getDate()}`
  } else {
    return `${startFormatted} - ${endFormatted}`
  }
}
